import { useState, useEffect, useRef, useCallback } from "react";

// Helper to parse system speech voices to resolve male/female voice options (prioritizing sweet & polite voices)
function getSystemVoice(gender) {
  if (typeof window === "undefined" || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  
  // List of known robotic/novelty voices on macOS/Windows that sound rough, mechanical, or unpleasant
  const noveltyVoices = [
    "fred", "novelty", "bells", "cellos", "pipe organ", "good news", "bad news",
    "deranged", "hysterical", "trinoids", "whisper", "zarvox", "boing", "bubbles",
    "chuckle", "orthography", "bells", "organ", "whisper"
  ];
  
  // Filter voices to English first, prioritizing local service voices that support boundary events
  // and completely excluding rough/robotic novelty voices.
  let enVoices = voices.filter(v => {
    const isEnglish = v.lang.startsWith("en-") || v.lang.startsWith("en_");
    const nameLower = v.name.toLowerCase();
    const isNovelty = noveltyVoices.some(n => nameLower.includes(n));
    return isEnglish && v.localService === true && !isNovelty;
  });

  if (enVoices.length === 0) {
    enVoices = voices.filter(v => {
      const isEnglish = v.lang.startsWith("en-") || v.lang.startsWith("en_");
      const nameLower = v.name.toLowerCase();
      const isNovelty = noveltyVoices.some(n => nameLower.includes(n));
      return isEnglish && !isNovelty;
    });
  }

  if (enVoices.length === 0) return null;
  
  if (gender === "female") {
    // Priority order for standard popular high-quality female voices
    const preferences = [
      "siri",
      "samantha", // Standard popular macOS/iOS voice (pre-installed, high quality)
      "zira",     // Standard popular Windows voice (pre-installed, high quality)
      "karen",    // Australia
      "tessa",    // South Africa
      "hazel",
      "veena",
      "fiona",
      "moira",
      "susan",
      "victoria"
    ];
    
    for (const name of preferences) {
      const found = enVoices.find(v => v.name.toLowerCase().includes(name));
      if (found) return found;
    }
    return enVoices[0]; // fallback
  } else {
    // Priority order for standard popular high-quality male voices
    const preferences = [
      "siri",
      "alex",     // Standard popular macOS/iOS voice (pre-installed, high quality)
      "david",    // Standard popular Windows voice (pre-installed, high quality)
      "daniel",   // UK English (gentle & polite)
      "george",
      "albert"
    ];
    
    for (const name of preferences) {
      const found = enVoices.find(v => v.name.toLowerCase().includes(name));
      if (found) return found;
    }
    return enVoices[0];
  }
}

function guessVoiceGender(voice) {
  const name = voice.name.toLowerCase();

  // Handle Siri voice variants specifically first to prevent classifying all Siri voices as female
  if (name.includes("siri")) {
    if (name.includes("voice 1") || name.includes("voice 3") || name.includes("voice1") || name.includes("voice3")) {
      return "male";
    }
    if (name.includes("voice 2") || name.includes("voice 4") || name.includes("voice2") || name.includes("voice4")) {
      return "female";
    }
  }

  const femalePatterns = [
    "samantha", "zira", "karen", "tessa", "hazel",
    "veena", "fiona", "moira", "susan", "victoria", "amber",
    "alloy", "aria", "nora", "celeste", "emma", "olivia",
    // Google/Android TTS female patterns
    "sfg", "fis", "ahp", "ctd", "mdf", "jef", "yom", "cob", "sif", "female"
  ];
  const malePatterns = [
    "alex", "david", "daniel", "george", "albert", "fred",
    "henry", "liam", "matt", "mark", "nick", "ethan", "james",
    // Google/Android TTS male patterns
    "iom", "tpd", "lts", "rgp", "rjs", "gfm", "bdf", "jdf", "olw", "caw", "kdb", "male"
  ];

  if (femalePatterns.some((pattern) => name.includes(pattern))) {
    return "female";
  }
  if (malePatterns.some((pattern) => name.includes(pattern))) {
    return "male";
  }
  return "female";
}

function groupVoicesByGender(voices) {
  const groups = { female: [], male: [], other: [] };
  voices.forEach((voice) => {
    const gender = guessVoiceGender(voice);
    if (gender === "female") groups.female.push(voice);
    else if (gender === "male") groups.male.push(voice);
    else groups.other.push(voice);
  });

  // Fallback: If one of the primary genders is empty but we have multiple voices,
  // distribute the voices based on index to ensure the user always has both Male and Female options.
  if (voices.length > 1 && (groups.female.length === 0 || groups.male.length === 0)) {
    groups.female = [];
    groups.male = [];
    groups.other = [];
    voices.forEach((voice, index) => {
      if (index % 2 === 0) {
        groups.female.push(voice);
      } else {
        groups.male.push(voice);
      }
    });
  }

  return groups;
}

export default function useReaderTTS({ pdf, pageNum, numPages, spreadMode, setPageNum, customText }) {
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceGender, setVoiceGender] = useState("female");
  const [selectedVoiceName, setSelectedVoiceName] = useState("");
  const [voices, setVoices] = useState([]);
  const [extractedText, setExtractedText] = useState("");
  const [loadingText, setLoadingText] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [speechBoundary, setSpeechBoundary] = useState({ charIndex: 0, charLength: 0 });

  const utteranceRef = useRef(null);
  const shouldAutoSpeakNextRef = useRef(false);
  const speechOffsetRef = useRef(0);
  const fallbackIntervalRef = useRef(null);

  // Ref to track isSpeaking without adding it as a useEffect dependency
  const isSpeakingRef = useRef(false);
  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);

  // Track the previous voice name so we only react to actual changes
  const prevVoiceNameRef = useRef("");

  const playDisabled = (typeof window === "undefined")
    ? true
    : (!extractedText || loadingText || !window.speechSynthesis);

  // Pre-load synthesis voices and keep the voice list available for the UI
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;

    const updateVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();
      const englishVoices = allVoices.filter((v) =>
        v.lang.startsWith("en-") || v.lang.startsWith("en_")
      );
      setVoices(englishVoices);
      setVoicesLoaded(englishVoices.length > 0);

      if (!selectedVoiceName && englishVoices.length > 0) {
        const defaultVoice = englishVoices.find((v) =>
          v.name.toLowerCase().includes("alex") ||
          v.name.toLowerCase().includes("david") ||
          v.name.toLowerCase().includes("samantha") ||
          v.name.toLowerCase().includes("siri")
        ) || englishVoices[0];

        setSelectedVoiceName(defaultVoice.name);
        setVoiceGender(guessVoiceGender(defaultVoice));
      }
    };

    const handleVoicesChanged = () => {
      updateVoices();
    };

    window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
    updateVoices();

    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
    };
  }, [selectedVoiceName]);

  const voiceGroups = groupVoicesByGender(voices);

  // Extract text from currently loaded pages
  useEffect(() => {
    if (customText !== undefined && customText !== null) {
      setExtractedText(customText);
      setLoadingText(false);
      return;
    }

    if (!pdf || !pageNum) return;

    let cancelled = false;
    setLoadingText(true);

    const pagesToExtract = [pageNum];
    if (spreadMode && (pageNum + 1) <= pdf.numPages) {
      pagesToExtract.push(pageNum + 1);
    }

    const promises = pagesToExtract.map((num) =>
      pdf.getPage(num).then((page) =>
        page.getTextContent().then((textContent) => {
          return textContent.items.map((item) => item.str).join(" ");
        })
      )
    );

    Promise.all(promises).then((texts) => {
      if (cancelled) return;
      const fullText = texts.join(" \n\n ");
      setExtractedText(fullText);
      setLoadingText(false);
    }).catch((err) => {
      console.error("[Reader] Text extraction failed:", err);
      if (!cancelled) {
        setExtractedText("");
        setLoadingText(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pdf, pageNum, spreadMode, customText]);

  const stopSpeech = useCallback((resetOffset = false) => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      window.currentUtterance = null;
    }
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    setIsSpeaking(false);
    setSpeechBoundary({ charIndex: 0, charLength: 0 });
    if (resetOffset) {
      speechOffsetRef.current = 0;
    }
  }, []);

  const startSpeech = useCallback(async (overrideOffset) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    if (!extractedText) {
      console.warn("[Reader] startSpeech called but no extractedText available");
      return;
    }

    const activeOffset = overrideOffset !== undefined ? overrideOffset : speechOffsetRef.current;

    const ensureVoicesLoaded = () => new Promise((resolve) => {
      try {
        const list = window.speechSynthesis.getVoices();
        if (list && list.length > 0) return resolve();
        const handler = () => { window.speechSynthesis.removeEventListener('voiceschanged', handler); resolve(); };
        window.speechSynthesis.addEventListener('voiceschanged', handler);
        setTimeout(resolve, 800);
      } catch (e) {
        resolve();
      }
    });

    await ensureVoicesLoaded();

    window.speechSynthesis.cancel(); // clear previous speech
    setSpeechBoundary({ charIndex: activeOffset, charLength: 0 });
    speechOffsetRef.current = activeOffset;

    const cleanText = extractedText
      .replace(/\s+/g, " ")
      .trim();

    if (!cleanText) return;

    const textToSpeak = cleanText.substring(activeOffset);
    if (!textToSpeak) return;

    const utterance = new SpeechSynthesisUtterance(textToSpeak);
    utteranceRef.current = utterance;
    if (typeof window !== "undefined") {
      window.currentUtterance = utterance;
    }

    const selectedVoice = voices.find((v) => v.name === selectedVoiceName);
    const voice = selectedVoice || getSystemVoice(voiceGender);
    if (voice) {
      utterance.voice = voice;
    }
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    let boundaryFired = false;
    utterance.onboundary = (event) => {
      if (utteranceRef.current !== utterance) return;
      boundaryFired = true;
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      if (event.name === "word") {
        const charIndex = event.charIndex;
        let charLength = event.charLength;
        if (!charLength) {
          let end = charIndex;
          while (end < textToSpeak.length && !/\s/.test(textToSpeak[end])) {
            end++;
          }
          charLength = end - charIndex;
        }
        const absoluteIndex = activeOffset + charIndex;
        setSpeechBoundary({
          charIndex: absoluteIndex,
          charLength: Math.max(1, charLength || 0)
        });
        speechOffsetRef.current = absoluteIndex;
      }
    };

    // Watchdog to start a word-by-word fallback interval if boundary events don't fire (common on mobile)
    if (fallbackIntervalRef.current) {
      clearInterval(fallbackIntervalRef.current);
      fallbackIntervalRef.current = null;
    }
    setTimeout(() => {
      if (!boundaryFired && isSpeakingRef.current && utteranceRef.current === utterance) {
        let currentAbsIndex = activeOffset;

        fallbackIntervalRef.current = setInterval(() => {
          if (!isSpeakingRef.current || utteranceRef.current !== utterance) {
            if (fallbackIntervalRef.current) {
              clearInterval(fallbackIntervalRef.current);
              fallbackIntervalRef.current = null;
            }
            return;
          }

          const textRemaining = cleanText.substring(currentAbsIndex);
          if (!textRemaining.trim()) {
            if (fallbackIntervalRef.current) {
              clearInterval(fallbackIntervalRef.current);
              fallbackIntervalRef.current = null;
            }
            return;
          }

          const spaceIdx = textRemaining.indexOf(" ");
          const nextWordLen = spaceIdx === -1 ? textRemaining.length : spaceIdx;

          currentAbsIndex += nextWordLen + 1; // advance past word and space

          setSpeechBoundary({
            charIndex: currentAbsIndex,
            charLength: Math.max(1, nextWordLen)
          });
          speechOffsetRef.current = currentAbsIndex;
        }, 320); // standard ~180 WPM
      }
    }, 1200);

    utterance.onend = () => {
      if (utteranceRef.current !== utterance) return;
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      setIsSpeaking(false);
      setSpeechBoundary({ charIndex: 0, charLength: 0 });
      speechOffsetRef.current = 0;
      if (typeof window !== "undefined") {
        window.currentUtterance = null;
      }
      if (pageNum < numPages) {
        shouldAutoSpeakNextRef.current = true;
        setPageNum((p) => Math.min(p + (spreadMode ? 2 : 1), numPages));
      }
    };

    utterance.onerror = (e) => {
      if (utteranceRef.current !== utterance) return;
      console.warn("[Reader] SpeechSynthesis utterance error:", e);
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      setIsSpeaking(false);
      setSpeechBoundary({ charIndex: 0, charLength: 0 });
      speechOffsetRef.current = 0;
      if (typeof window !== "undefined") {
        window.currentUtterance = null;
      }
    };

    setIsSpeaking(true);
    try {
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn('[Reader] speak() failed:', e);
      setIsSpeaking(false);
    }
  }, [extractedText, pageNum, numPages, spreadMode, selectedVoiceName, voiceGender, voices, setPageNum]);

  const toggleSpeech = useCallback(() => {
    if (isSpeaking) {
      stopSpeech();
    } else {
      if (playDisabled) {
        console.warn("[Reader] Can't start speech: text or speech API not ready");
        return;
      }
      startSpeech();
    }
  }, [isSpeaking, playDisabled, startSpeech, stopSpeech]);

  const stopSpeechOnNavigation = useCallback(() => {
    shouldAutoSpeakNextRef.current = false;
    stopSpeech(true);
  }, [stopSpeech]);

  // Autoplay next page hook
  useEffect(() => {
    if (extractedText && shouldAutoSpeakNextRef.current) {
      shouldAutoSpeakNextRef.current = false;
      const t = setTimeout(() => {
        startSpeech();
      }, 500);
      return () => clearTimeout(t);
    }
  }, [extractedText, startSpeech]);

  // ── Voice hot-swap: restart immediately when voice changes mid-speech ──────
  // Without this, the new voice only applies after the user manually pauses
  // and replays. Now we cancel and resume at the exact same word offset.
  useEffect(() => {
    if (!selectedVoiceName) return;
    if (prevVoiceNameRef.current === selectedVoiceName) return;
    prevVoiceNameRef.current = selectedVoiceName;

    if (!isSpeakingRef.current) return; // only act when actively speaking

    const offset = speechOffsetRef.current;
    window.speechSynthesis.cancel();
    // startSpeech is async; its internal `await ensureVoicesLoaded()` provides
    // a microtask gap after cancel() — no extra setTimeout needed.
    startSpeech(offset);
  }, [selectedVoiceName, startSpeech]);

  return {
    showAudioPlayer,
    setShowAudioPlayer,
    isSpeaking,
    voiceGender,
    setVoiceGender,
    selectedVoiceName,
    setSelectedVoiceName,
    voices,
    extractedText,
    loadingText,
    voicesLoaded,
    speechBoundary,
    playDisabled,
    voiceGroups,
    stopSpeech,
    startSpeech,
    toggleSpeech,
    stopSpeechOnNavigation
  };
}
